import React, {useState, useMemo} from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SectorTable = ({sectors, onSectorSelect, selectedSectorId, loading}) => {
    const [sortConfig, setSortConfig] = useState({key: 'name', direction: 'asc'});

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({key, direction});
    };

    const sortedSectors = React.useMemo(() => {
        if (!sortConfig.key) return sectors;

        return [...sectors].sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [sectors, sortConfig]);

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <Icon name="ArrowUpDown" size={16} className="text-muted-foreground"/>;
        }
        return sortConfig.direction === 'asc'
            ? <Icon name="ArrowUp" size={16} className="text-primary"/>
            : <Icon name="ArrowDown" size={16} className="text-primary"/>;
    };

    if (loading) {
        return (
            <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="p-6">
                    <div className="animate-spiritual-pulse space-y-4">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="h-12 bg-muted rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-card rounded-lg border border-border overflow-hidden shadow-spiritual-sm">
            <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="font-heading font-semibold text-lg text-foreground">
                            Sector-wise Allocation
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Click on a sector to view detailed member allocations
                        </p>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Icon name="Users" size={16}/>
                        <span>{sectors.length} sectors</span>
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto">
                <div className="table table-fixed w-full sector-table">
                    <div className="table-header-group bg-muted/50">
                        <div className="table-row">
                            <div className="table-cell text-left p-4 font-heading font-medium text-foreground">
                                <button
                                    onClick={() => handleSort('name')}
                                    className="flex items-center space-x-2 hover:text-primary transition-colors"
                                >
                                    <span>Sector Name</span>
                                    {getSortIcon('name')}
                                </button>
                            </div>
                            <div className="table-cell text-left p-4 font-heading font-medium text-foreground">
                                <button
                                    onClick={() => handleSort('leader1')}
                                    className="flex items-center space-x-2 hover:text-primary transition-colors"
                                >
                                    <span>Leader 1</span>
                                    {/* {getSortIcon('leader1')} */}
                                </button>
                            </div>
                            <div className="table-cell text-left p-4 font-heading font-medium text-foreground">
                                <button
                                    onClick={() => handleSort('leader2')}
                                    className="flex items-center space-x-2 hover:text-primary transition-colors"
                                >
                                    <span>Leader 2</span>
                                    {/* {getSortIcon('leader2')} */}
                                </button>
                            </div>
                            <div className="table-cell text-center p-4 font-heading font-medium text-foreground">
                                <button
                                    onClick={() => handleSort('count')}
                                    className="flex items-center space-x-2 hover:text-primary transition-colors mx-auto"
                                >
                                    <span>Allocated / Required Count</span>
                                    {getSortIcon('count')}
                                </button>
                            </div>
                            <div className="table-cell text-center p-4 font-heading font-medium text-foreground">
                                Actions
                            </div>
                        </div>
                    </div>
                    <div className="table-row-group">
                        {sortedSectors.map((sector, index) => (
                            <div
                                key={sector._id}
                                className={`
                                  table-row border-b border-border hover:bg-muted/30 transition-colors cursor-pointer
                                  ${selectedSectorId === sector._id ? 'bg-primary/5 border-primary/20' : ''}
                                  ${index % 2 === 0 ? 'zebra-stripe' : ''}
                                `}
                                onClick={() => onSectorSelect(sector)}
                            >
                                <div className="table-cell p-4" data-title="Sector Name">
                                    <div className="flex items-center space-x-3">
                                        <div
                                            className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                            <Icon name="Building2" size={20} className="text-primary"/>
                                        </div>
                                        <div>
                                            <p className="font-heading font-medium text-foreground">
                                                {sector.name}
                                            </p>
                                            {/* <p className="text-sm text-muted-foreground">
                                                {sector.name}
                                            </p> */}
                                        </div>
                                    </div>
                                </div>
                                <div className="table-cell p-4" data-title="Leader 1">
                                    <div className="flex items-center space-x-2">
                                        <div
                                            className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center">
                                            <Icon name="User" size={16} className="text-success"/>
                                        </div>
                                        <div>
                                            <p className="font-medium text-foreground">{sector.leader1?.name || "-----"}</p>
                                            <p className="text-xs text-muted-foreground">{sector.leader1?.mobile || "-----"}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="table-cell p-4" data-title="Leader 2">
                                    <div className="flex items-center space-x-2">
                                        <div
                                            className="w-8 h-8 bg-warning/10 rounded-full flex items-center justify-center">
                                            <Icon name="User" size={16} className="text-warning"/>
                                        </div>
                                        <div>
                                            <p className="font-medium text-foreground">{sector.leader2?.name || "-----"}</p>
                                            <p className="text-xs text-muted-foreground">{sector.leader2?.mobile || "-----"}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="table-cell p-4 text-center" data-title="Allocated Count">
                                    <div className="inline-flex items-center space-x-2">
                                        <span className="text-2xl font-heading font-semibold text-success">
                                          {sector.count}
                                        </span>
                                        <span>/</span>
                                        <span className="text-2xl font-heading font-semibold text-primary">
                                          {sector.reqCount}
                                        </span>
                                        {/* <span className="text-sm text-muted-foreground">
                                          / {0}
                                        </span> */}
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        {/* <div
                                      className="bg-primary rounded-full h-2 transition-all duration-300"
                                      style={{
                                        width: `${Math.min((sector.allocatedCount / sector.requiredCount) * 100, 100)}%`
                                      }}
                                    ></div> */}
                                    </div>
                                </div>
                                <div className="table-cell p-4 text-center">
                                    <div className="flex items-center justify-center space-x-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSectorSelect(sector);
                                            }}
                                            iconName="Eye"
                                            iconPosition="left"
                                            className="text-sm inline-flex w-full md:w-auto items-center justify-center"
                                        >
                                            View Details
                                        </Button>
                                        {selectedSectorId === sector._id && (
                                            <div
                                                className="w-2 h-2 bg-primary rounded-full animate-spiritual-pulse"></div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {sortedSectors.length === 0 && (
                <div className="text-center py-12">
                    <Icon name="Building2" size={48} className="text-muted-foreground mx-auto mb-4"/>
                    <p className="text-muted-foreground">No sectors found</p>
                </div>
            )}
        </div>
    );
};

export default SectorTable;